/**
 * VÉRAULT Member Suite — Experience Overview (Wave 26.21 Phase 2a).
 * ───────────────────────────────────────────────────────────────────
 * The emotional and operational pulse of an Experience. Six zones
 * flow vertically as one editorial page:
 *
 *   Zone 1 — Cinematic hero (title · dates · travelers · state)
 *   Zone 2 — Important updates  (silent when empty)
 *   Zone 3 — Smart status strip (priority-sorted; swipeable on mobile)
 *   Zone 4 — Experience flow preview (next emotional moments)
 *   Zone 5 — Curated note / letter
 *   Zone 6 — Memory preview (visual architecture only)
 *
 * Phase 2a builds the LAYOUT + ARCHITECTURE. Phase 2b plugs the
 * backend aggregator (`/api/client-portal/experiences/:id/overview`),
 * secret-day placeholders, and reveal-state wiring.
 */
import React from "react";
import { useOutletContext, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Bell, MessageSquare, Compass, ScrollText, Sparkles, Wallet, ShieldCheck, Plane, Heart } from "lucide-react";
import { Heading, EditorialRule, fmt, api, motion as portalMotion } from "../../../index";
import { useDestinationVeil } from "../../../../hooks/useDestinationVeil";
import { getTripTimezone, getLocalDateISO } from "../../../lib/timezone";
import { deriveExperienceState, getStateLabel } from "../../../../hooks/useExperienceState";
import SecretDayCard from "./components/SecretDayCard";
import LocalTimePill from "./editorial/LocalTimePill";
import RevealCountdownChip from "./editorial/RevealCountdownChip";

const { fadeInUp, stagger } = portalMotion;

// ═══════════════════════════════════════════════════════════════════
// useVeilLift — detect the moment veiled flips true → false.
// Returns `liftMoment: boolean` that stays `true` for ~5s after the
// transition, then falls quietly back. Tracks the previous state in a
// ref so a fresh page load with veil already lifted does NOT trigger.
// ═══════════════════════════════════════════════════════════════════
function useVeilLift(veiled) {
  const prevRef = React.useRef(veiled);
  const [liftMoment, setLiftMoment] = React.useState(false);
  React.useEffect(() => {
    if (prevRef.current === true && veiled === false) {
      setLiftMoment(true);
      const t = setTimeout(() => setLiftMoment(false), 5200);
      prevRef.current = veiled;
      return () => clearTimeout(t);
    }
    prevRef.current = veiled;
    return undefined;
  }, [veiled]);
  return liftMoment;
}

// ═══════════════════════════════════════════════════════════════════
// Zone 1 — Cinematic hero
// ═══════════════════════════════════════════════════════════════════
function HeroZone({ trip }) {
  const veil = useDestinationVeil(trip);
  const { state, daysToStart, daysToEnd } = deriveExperienceState(trip);
  const stateInfo = getStateLabel(state);
  const liftMoment = useVeilLift(veil.veiled);

  // Countdown line — quiet, calm, never numerical-noise.
  // Wave 30 · Phase 7d — lifecycle-aware emotional pacing.
  let countdown = null;
  if (state === "planning") countdown = "Quietly composing.";
  else if (state === "confirmed" && daysToStart != null) countdown = `${daysToStart} days from today.`;
  else if (state === "preparing" && daysToStart != null) {
    if (daysToStart === 0) countdown = "Departure today.";
    else if (daysToStart === 1) countdown = "Tomorrow you go.";
    else if (daysToStart <= 3) countdown = `${daysToStart} days until you go · gentle preparations.`;
    else countdown = `${daysToStart} days until you go.`;
  }
  else if (state === "traveling" && daysToEnd != null) {
    countdown = daysToEnd === 0
      ? "Your final day."
      : `Day ${(trip.trip_start_date || trip.start_date) ? Math.max(1, 1 + Math.floor((Date.now() - new Date(trip.trip_start_date || trip.start_date)) / (1000 * 60 * 60 * 24))) : "—"} of the journey.`;
  }
  else if (state === "completed") countdown = "A chapter closed · your memories are below.";
  else if (state === "welcome_home") countdown = "Welcome home. The journey came back with you.";
  else if (state === "memories") countdown = "A quiet shelf · revisit whenever you like.";

  const eyebrowText = `${stateInfo.label}${veil.veiled ? " · Awaiting reveal" : ""}`;
  const easeCalm = [0.22, 0.61, 0.36, 1];

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: easeCalm }}
      data-testid="overview-hero"
      data-veil-lift={liftMoment ? "true" : "false"}
      data-veiled={veil.veiled ? "true" : "false"}
      className="relative overflow-hidden rounded-md -mx-5 sm:mx-0"
    >
      <div className="aspect-[5/6] sm:aspect-[21/10] relative bg-[hsl(var(--background-stone))]">
        {/* Cross-fading hero image (atmospheric ↔ real) */}
        <AnimatePresence initial={false} mode="sync">
          {veil.image ? (
            <motion.img
              key={veil.image}
              src={veil.image}
              alt={veil.label}
              loading="eager"
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: easeCalm }}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="overview-hero-image"
            />
          ) : (
            <motion.div
              key="hero-placeholder"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.0, ease: easeCalm }}
              className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--background-paper))] via-[hsl(var(--background-stone))] to-[hsl(var(--background-paper))]"
            />
          )}
        </AnimatePresence>

        {/* Cross-fading overlay — atmospheric trips get a touch more diffusion */}
        <AnimatePresence initial={false} mode="sync">
          <motion.div
            key={veil.atmosphere ? "atmospheric-overlay" : "real-overlay"}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.0, ease: easeCalm }}
            className={`absolute inset-0 ${veil.atmosphere ? "bg-gradient-to-t from-black/70 via-black/30 to-black/10" : "bg-gradient-to-t from-black/65 via-black/15 to-transparent"}`}
          />
        </AnimatePresence>

        {/* One-time reveal pulse — gold shimmer + "Revealed" word, fades after ~5s */}
        <AnimatePresence>
          {liftMoment && (
            <motion.div
              key="veil-lift-pulse"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: easeCalm }}
              className="absolute inset-0 pointer-events-none"
              data-testid="veil-lift-pulse"
            >
              {/* Wave 11n — Curtain sweep. Two opaque cream panels meet
                  in the middle, then sweep apart in 1.6s to "open" the
                  reveal. Sits ABOVE the shimmer so the gold breathes
                  in afterwards. */}
              <motion.div
                key="curtain-left"
                initial={{ x: 0 }}
                animate={{ x: "-100%" }}
                transition={{ duration: 1.6, ease: [0.65, 0.05, 0.36, 1], delay: 0.15 }}
                className="absolute inset-y-0 left-0 w-1/2 bg-[hsl(var(--background-paper))]"
                style={{ boxShadow: "8px 0 20px rgba(0,0,0,0.18)" }}
                data-testid="veil-curtain-left"
              />
              <motion.div
                key="curtain-right"
                initial={{ x: 0 }}
                animate={{ x: "100%" }}
                transition={{ duration: 1.6, ease: [0.65, 0.05, 0.36, 1], delay: 0.15 }}
                className="absolute inset-y-0 right-0 w-1/2 bg-[hsl(var(--background-paper))]"
                style={{ boxShadow: "-8px 0 20px rgba(0,0,0,0.18)" }}
                data-testid="veil-curtain-right"
              />
              {/* soft gold wash that breathes once */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 0.55, 0.25, 0.4, 0] }}
                transition={{ duration: 4.8, ease: easeCalm, times: [0, 0.18, 0.45, 0.7, 1], delay: 0.9 }}
                className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--accent-gold))]/35 via-[hsl(var(--accent-gold))]/10 to-transparent mix-blend-screen"
              />
              {/* quiet 'Revealed' chip — top center */}
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.8, ease: easeCalm, delay: 0.3 }}
                className="absolute top-5 sm:top-7 left-1/2 -translate-x-1/2"
              >
                <span
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[hsl(var(--accent-gold))]/60 bg-black/30 backdrop-blur-sm font-mono text-[10px] uppercase tracking-[0.28em] text-white"
                  data-testid="veil-lift-label"
                >
                  <Sparkles className="size-3 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
                  Revealed
                </span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Wave 30 · Phase 7e — Reveal countdown chip (silent unless veiled
            + destination_reveal_at within 48h). Sits top-center over the
            hero, dovetailing into the veil-lift "Revealed" chip when the
            moment passes. */}
        <RevealCountdownChip trip={trip} veiled={veil.veiled} />

        {/* Caption */}
        <div className="absolute inset-x-0 bottom-0 p-6 sm:p-12 text-white">
          {/* Wave 30 · Phase 7d — Local time pill (silent when veiled
              or not in preparing/traveling). Sits just above the eyebrow
              so it carries a quiet "you're aware of where you are" cue. */}
          <div className="mb-3">
            <LocalTimePill trip={trip} veiled={veil.veiled} state={state} />
          </div>
          <AnimatePresence initial={false} mode="wait">
            <motion.p
              key={eyebrowText}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 0.85, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.6, ease: easeCalm }}
              className="font-mono text-[10px] uppercase tracking-[0.28em]"
              data-testid="overview-hero-eyebrow"
            >
              {eyebrowText}
            </motion.p>
          </AnimatePresence>
          <AnimatePresence initial={false} mode="wait">
            <motion.h1
              key={veil.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.8, ease: easeCalm }}
              className="mt-3 font-serif text-[28px] sm:text-[44px] lg:text-[56px] leading-[1.05] tracking-tight max-w-3xl"
              data-testid="overview-hero-title"
            >
              {veil.label}
            </motion.h1>
          </AnimatePresence>
          {veil.dateLabel && (
            <AnimatePresence initial={false} mode="wait">
              <motion.p
                key={veil.dateLabel}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 0.9, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.7, ease: easeCalm, delay: 0.05 }}
                className="mt-2 font-accent italic text-[14px] sm:text-[17px]"
              >
                {veil.dateLabel}
              </motion.p>
            </AnimatePresence>
          )}
          {countdown && (
            <p className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.22em] opacity-75">
              {countdown}
            </p>
          )}
        </div>
      </div>
    </motion.section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Zone 2 — Important updates  (silent when empty)
// ═══════════════════════════════════════════════════════════════════
function ImportantUpdatesZone({ updates }) {
  if (!updates || updates.length === 0) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
      data-testid="overview-updates"
      className="border border-[hsl(var(--accent-gold))]/30 bg-[hsl(var(--accent-gold))]/[0.05] rounded-md p-5 sm:p-6 space-y-4"
    >
      <div className="flex items-center gap-2">
        <Bell className="size-3.5 text-[hsl(var(--accent-gold))]" strokeWidth={1.5} />
        <h2 className="font-mono text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--accent-gold))]">Important updates</h2>
      </div>
      <ul className="space-y-3">
        {updates.map((u) => (
          <li key={u.id || u.label} className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="font-serif text-[16px] text-[hsl(var(--foreground))] leading-snug">{u.label}</p>
              {u.detail && <p className="mt-0.5 font-accent italic text-[13px] text-[hsl(var(--foreground-muted))]">{u.detail}</p>}
            </div>
            {u.to && (
              <Link to={u.to} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-secondary))] hover:text-[hsl(var(--accent-gold))] transition-colors">
                Open
                <ArrowUpRight className="size-3" strokeWidth={1.5} />
              </Link>
            )}
          </li>
        ))}
      </ul>
    </motion.section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Zone 3 — Smart status strip
// ═══════════════════════════════════════════════════════════════════
function SmartStatusStrip({ items, testid = "overview-status-strip" }) {
  if (!items || items.length === 0) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
      data-testid={testid}
      className="-mx-5 sm:mx-0"
    >
      <div className="px-5 sm:px-0 flex gap-3 overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:snap-none pb-1 no-scrollbar">
        {items.map((it, i) => (
          <Link
            key={it.id || i}
            to={it.to || "#"}
            className="shrink-0 snap-start w-[80%] sm:w-auto sm:flex-1 px-5 py-4 rounded-md bg-white/60 backdrop-blur-[2px] border border-[rgba(0,0,0,0.05)] hover:border-[hsl(var(--accent-gold))]/35 transition-colors"
            data-testid={`status-cell-${it.id || i}`}
          >
            <div className="flex items-center gap-2 mb-1.5">
              {it.Icon && <it.Icon className="size-3.5 text-[hsl(var(--foreground-muted))]" strokeWidth={1.5} />}
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
                {it.eyebrow}
              </span>
            </div>
            <p className="font-serif text-[18px] text-[hsl(var(--foreground))] leading-snug tracking-tight">
              {it.title}
            </p>
            {it.hint && (
              <p className="mt-1 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))]">
                {it.hint}
              </p>
            )}
          </Link>
        ))}
      </div>
    </motion.section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Zone 4 — Experience flow preview
// ═══════════════════════════════════════════════════════════════════
function FlowPreviewZone({ trip, flowPreview, nextDay, nextIdx }) {
  const lead = flowPreview?.lead;
  const moments = flowPreview?.moments || [];

  // Split into secret moments (rendered as SecretDayCard) and regular
  // moments (rendered as quiet rows). Preserves visual variety without
  // overwhelming any one zone.
  const secretMoments = moments.filter((m) => m.kind === "secret_reveal" || m.kind === "secret_day");
  const openMoments = moments.filter((m) => m.kind === "moment");

  if (!lead && moments.length === 0 && !nextDay) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1] }}
      data-testid="overview-flow-preview"
      className="space-y-4"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          What comes next
        </h2>
        <Link
          to={`/portal/client/experiences/${trip.id}/itinerary`}
          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors"
          data-testid="overview-flow-open-itinerary"
        >
          Open itinerary
          <ArrowUpRight className="size-3" strokeWidth={1.5} />
        </Link>
      </div>

      {/* Next day, summarized — tap to jump straight to it in the itinerary. */}
      {nextDay && (
        <div className="space-y-1.5" data-testid="overview-next-day">
          <p className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))]">
            Tomorrow{nextDay.date ? ` · ${new Date(nextDay.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}` : ""}
          </p>
          <DaySummaryCard trip={trip} day={nextDay} idx={nextIdx} />
        </div>
      )}

      {lead && (
        <p className="font-serif text-[20px] sm:text-[22px] leading-snug tracking-tight text-[hsl(var(--foreground))] max-w-2xl">
          {lead}
        </p>
      )}

      {/* Secret days first — visually distinctive, dark cards */}
      {secretMoments.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4" data-testid="overview-secret-moments">
          {secretMoments.map((m) => (
            <SecretDayCard key={`secret-${m.day_number}`} moment={m} tripId={trip.id} />
          ))}
        </div>
      )}

      {/* Open (revealed) days — quiet two-column list */}
      {openMoments.length > 0 && (
        <ul className="border-t border-[rgba(0,0,0,0.05)] divide-y divide-[rgba(0,0,0,0.05)]" data-testid="overview-open-moments">
          {openMoments.map((m) => (
            <li key={`moment-${m.day_number}`}>
              <Link
                to={`/portal/client/experiences/${trip.id}/itinerary#day-${m.day_number}`}
                className="flex items-baseline justify-between gap-4 py-3 group"
                data-testid={`overview-moment-${m.day_number}`}
              >
                <div className="flex items-baseline gap-3 min-w-0">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] w-14 shrink-0">
                    Day {m.day_number}
                  </span>
                  <span className="font-serif text-[16px] sm:text-[17px] text-[hsl(var(--foreground))] leading-snug truncate group-hover:text-[hsl(var(--accent-gold))] transition-colors">
                    {m.label}
                  </span>
                </div>
                <ArrowUpRight className="size-3.5 text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--accent-gold))] transition-colors shrink-0" strokeWidth={1.5} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </motion.section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Zone 5 — Curated note / letter
// ═══════════════════════════════════════════════════════════════════
function CuratedNoteZone({ trip, serverNote }) {
  // Prefer the server-canonicalized note from the aggregator; fall
  // back to whatever the trip object happens to carry.
  const note = serverNote || trip?.client_note || trip?.advisor_welcome_note || trip?.curated_note;
  if (!note || !note.trim()) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
      data-testid="overview-curated-note"
      className="border-l-2 border-[hsl(var(--accent-gold))] pl-5 sm:pl-6 py-2 max-w-2xl"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.26em] text-[hsl(var(--foreground-muted))]">
        From your advisor
      </p>
      <p className="mt-3 font-accent italic text-[16px] sm:text-[17px] leading-relaxed text-[hsl(var(--foreground-secondary))] whitespace-pre-line">
        {note.trim()}
      </p>
      <p className="mt-4 font-serif text-[14px] tracking-tight text-[hsl(var(--foreground))]">
        — Antonia
      </p>
    </motion.section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Zone 6 — Memory preview (architecture only)
// ═══════════════════════════════════════════════════════════════════
function MemoryPreviewZone({ trip, memoryCount = 0 }) {
  const { state } = deriveExperienceState(trip);
  // Only surface during/after travel, per spec.
  if (state !== "traveling" && state !== "completed") return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
      data-testid="overview-memory-preview"
      className="space-y-3"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--foreground-muted))]">
          Memories{memoryCount > 0 ? ` · ${memoryCount}` : ""}
        </h2>
        <Link to={`/portal/client/experiences/${trip.id}/memories`} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors">
          Open memories
          <ArrowUpRight className="size-3" strokeWidth={1.5} />
        </Link>
      </div>
      <div className="aspect-[16/7] rounded-md bg-gradient-to-br from-[hsl(var(--background-paper))]/60 to-[hsl(var(--background-stone))]/40 flex items-center justify-center">
        <p className="font-accent italic text-[14px] text-[hsl(var(--foreground-muted))] max-w-md text-center px-6">
          {memoryCount === 0
            ? "Your memories will live here — photos, journals, and moments saved along the way."
            : `${memoryCount} ${memoryCount === 1 ? "moment" : "moments"} saved so far.`}
        </p>
      </div>
    </motion.section>
  );
}

// Shared clickable day-summary box. Tapping it deep-links into the
// itinerary and opens that exact day (#day-N handled by ItineraryView).
function DaySummaryCard({ trip, day, idx }) {
  const events = (day.items || []).slice(0, 4);
  return (
    <Link
      to={`/portal/client/experiences/${trip.id}/itinerary#day-${idx + 1}`}
      className="block rounded-md border border-[rgba(0,0,0,0.06)] bg-white/50 p-4 sm:p-5 space-y-2 hover:border-[hsl(var(--accent-gold))]/40 transition-colors group"
      data-testid={`overview-day-summary-${idx}`}
    >
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-serif text-[20px] sm:text-[24px] leading-tight group-hover:text-[hsl(var(--accent-gold))] transition-colors">{day.title || "Your day"}</h3>
        <ArrowUpRight className="size-4 text-[hsl(var(--foreground-muted))] group-hover:text-[hsl(var(--accent-gold))] transition-colors shrink-0 mt-1" strokeWidth={1.5} />
      </div>
      {(day.day_style || day.description) && (
        <p className="font-accent italic text-[13.5px] leading-relaxed text-[hsl(var(--foreground-secondary))]">{day.day_style || day.description}</p>
      )}
      {events.length > 0 && (
        <ul className="pt-1 space-y-1.5">
          {events.map((it, i) => (
            <li key={it.id || i} className="flex items-baseline gap-2 font-serif text-[13.5px] text-[hsl(var(--foreground-secondary))]" data-testid={`overview-day-summary-${idx}-event-${i}`}>
              {it.time && <span className="font-mono text-[9.5px] uppercase tracking-[0.12em] text-[hsl(var(--foreground-muted))] shrink-0">{it.time}</span>}
              <span>{it.title}</span>
            </li>
          ))}
        </ul>
      )}
    </Link>
  );
}

// Today's day — a calm lead summarizing today's plan (Lisbon time).
function TodayZone({ trip, day, idx, label }) {
  if (!day) return null;
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 0.61, 0.36, 1] }}
      className="space-y-3" data-testid="overview-today">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="font-mono text-[10px] uppercase tracking-[0.28em] text-[hsl(var(--accent-gold))]">
          {label}{day.date ? ` · ${new Date(day.date + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}` : ""}
        </h2>
        <Link to={`/portal/client/experiences/${trip.id}/itinerary#day-${idx + 1}`} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--foreground-muted))] hover:text-[hsl(var(--accent-gold))] transition-colors" data-testid="overview-today-open">
          Open itinerary <ArrowUpRight className="size-3" strokeWidth={1.5} />
        </Link>
      </div>
      <DaySummaryCard trip={trip} day={day} idx={idx} />
    </motion.section>
  );
}


// ═══════════════════════════════════════════════════════════════════
// Page
// ═══════════════════════════════════════════════════════════════════
export default function ExperienceOverview() {
  const { trip } = useOutletContext() || {};

  // Itinerary fetch (once) — powers the Today card + next-day summary.
  const [itin, setItin] = React.useState(null);
  React.useEffect(() => {
    if (!trip?.id) return undefined;
    let alive = true;
    api.get(`/client-portal/trips/${trip.id}/design-studio-itinerary`)
      .then(({ data }) => alive && setItin(data)).catch(() => {});
    return () => { alive = false; };
  }, [trip?.id]);

  const dayInfo = React.useMemo(() => {
    const dz = itin?.days || [];
    if (!dz.length) return null;
    const tripTz = getTripTimezone(trip);
    let idx = dz.findIndex((d) => d.date === getLocalDateISO(d.timezone || tripTz));
    let label = "Today";
    if (idx < 0) {
      const today = getLocalDateISO(tripTz);
      idx = dz.findIndex((d) => (d.date || "") >= today);
      if (idx < 0) return null;
      label = "Up next";
    }
    const nextIdx = idx + 1 < dz.length ? idx + 1 : null;
    return { idx, label, day: dz[idx], nextIdx, nextDay: nextIdx != null ? dz[nextIdx] : null };
  }, [itin, trip]);

  // Wave 26.21 Phase 2b — single aggregator call. The aggregator
  // returns derived_state, important_updates, flow_preview, curated_note,
  // reveal_state, and memory_count in one round-trip. We treat it as
  // PROGRESSIVE ENHANCEMENT: if it fails, we fall back to the Phase 2a
  // client-side inference so the page never breaks.
  const [overview, setOverview] = React.useState(null);
  // Wave 26.22 · refetch the aggregator whenever the trip's reveal posture
  // changes — that way the secret-day list and reveal_state stay aligned
  // with the just-lifted hero.
  React.useEffect(() => {
    if (!trip?.id) return undefined;
    let alive = true;
    api.get(`/client-portal/experiences/${trip.id}/overview`)
      .then(({ data }) => alive && setOverview(data))
      .catch(() => { /* progressive fallback */ });
    return () => { alive = false; };
  }, [trip?.id, trip?.reveal_destination, trip?.reveal_dates]);

  // Important updates — prefer server-computed list when present.
  const updates = React.useMemo(() => {
    if (!trip) return [];
    if (overview?.important_updates) {
      return overview.important_updates.map((u) => ({
        id: u.id,
        label: u.label,
        detail: u.detail,
        to: u.deep_link,
      }));
    }
    const list = [];
    if (trip.outstanding_balance && Number(trip.outstanding_balance) > 0) {
      list.push({
        id: "balance",
        label: `Final balance due${trip.outstanding_balance ? ` — ${fmt.money(trip.outstanding_balance)}` : ""}`,
        detail: "Open Essentials to review.",
        to: `/portal/client/experiences/${trip.id}/essentials`,
      });
    }
    if (trip.pending_signature_count && Number(trip.pending_signature_count) > 0) {
      list.push({
        id: "signature",
        label: `${trip.pending_signature_count} signature${trip.pending_signature_count > 1 ? "s" : ""} awaiting your review`,
        to: `/portal/client/experiences/${trip.id}/essentials`,
      });
    }
    if (trip.passport_action_required) {
      list.push({
        id: "passport",
        label: "A passport detail needs your attention",
        to: `/portal/client/experiences/${trip.id}/essentials`,
      });
    }
    return list;
  }, [trip, overview]);

  // Smart status strip — derived from state + existing fields.
  const statusItems = React.useMemo(() => {
    if (!trip) return [];
    const items = [];
    const { state } = deriveExperienceState(trip);
    if (state === "preparing" || state === "confirmed") {
      items.push({
        id: "prep",
        eyebrow: "Before you go",
        title: "Your travel prep",
        hint: "Checklists, packing, and final touches.",
        to: `/portal/client/experiences/${trip.id}/travel-prep`,
        Icon: ShieldCheck,
      });
    }
    items.push({
      id: "itinerary",
      eyebrow: "The journey",
      title: "Your itinerary",
      hint: state === "traveling" ? "Today, and what's next." : "Day by day.",
      to: `/portal/client/experiences/${trip.id}/itinerary`,
      Icon: Compass,
    });
    items.push({
      id: "essentials",
      eyebrow: "Operational",
      title: "Essentials",
      hint: "Documents, balances, travelers.",
      to: `/portal/client/experiences/${trip.id}/essentials`,
      Icon: Wallet,
    });
    items.push({
      id: "messages",
      eyebrow: "Talk",
      title: "Message your advisor",
      hint: "A quiet line, always open.",
      to: `/portal/client/messages`,
      Icon: MessageSquare,
    });
    // Wave 36 · Phase 1 · PI-7 — Welcome Home / Memories surface.
    // When the advisor advances the trip (or the auto-trigger fires at
    // end_date), the client gets a calm dedicated landing.
    if (state === "welcome_home" || state === "memories") {
      items.unshift({
        id: "welcome_home",
        eyebrow: state === "memories" ? "Quiet shelf" : "Welcome home",
        title: state === "memories" ? "Your memories suite" : "You're home",
        hint: state === "memories"
          ? "A quiet shelf · revisit whenever you like."
          : "The journey came back with you.",
        to: `/portal/client/experiences/${trip.id}/welcome`,
        Icon: Heart,
      });
    }
    return items;
  }, [trip]);

  if (!trip) return null;

  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={stagger}
      className="space-y-9 sm:space-y-12 pb-12"
      data-testid="overview-root"
      data-reveal-masked={overview?.reveal_state?.destination_masked ? "true" : "false"}
      data-any-secret-days={overview?.reveal_state?.any_secret_days ? "true" : "false"}
    >
      <HeroZone trip={trip} />
      <TodayZone trip={trip} day={dayInfo?.day} idx={dayInfo?.idx} label={dayInfo?.label} />
      <ImportantUpdatesZone updates={updates} />
      <SmartStatusStrip items={statusItems} />
      <FlowPreviewZone trip={trip} flowPreview={overview?.flow_preview} nextDay={dayInfo?.nextDay} nextIdx={dayInfo?.nextIdx} />
      <CuratedNoteZone trip={trip} serverNote={overview?.curated_note} />
      <MemoryPreviewZone trip={trip} memoryCount={overview?.memory_count || 0} />
    </motion.div>
  );
}
